import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Api from "../../api";
import { useData } from "../../context/DataContext";
import { ToastContainer, toast } from 'react-toastify';
import ImageUpload from "../ImageUpload";
import { prepareImageForUpload } from "../../utils/imageCompress";
import { SipDropdown, SipTextarea } from "../form/SipControls";

function TindakanComponent({ id, tgl }) {
  const { petugas, getPetugas } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    jenis_kerusakan: "",
    deskripsi: "",
    tingkat_prioritas: "sedang",
    foto: null,
    tgl_masuk: tgl,
    inventaris_id: id,
    petugas: ""
  });

  const openModal = () => setIsModalOpen(true);
  
  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsClosing(false);
      setIsLoading(false);
      setFormData({
        jenis_kerusakan: "",
        deskripsi: "",
        tingkat_prioritas: "sedang",
        foto: null,
        tgl_masuk: tgl,
        inventaris_id: id,
        petugas: ""
      });
    }, 500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (file) => {
    setFormData(prev => ({
      ...prev,
      foto: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('jenis_kerusakan', formData.jenis_kerusakan);
      submitData.append('deskripsi', formData.deskripsi);
      submitData.append('tingkat_prioritas', formData.tingkat_prioritas);
      submitData.append('tgl_masuk', formData.tgl_masuk);
      submitData.append('inventaris_id', formData.inventaris_id);
      submitData.append('pic', formData.petugas);
      
      if (formData.foto instanceof File) {
        const foto = await prepareImageForUpload(formData.foto);
        if (foto) submitData.append('foto', foto);
      }

      const response = await Api.post('/mon/tindakan', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Laporan kerusakan berhasil dikirim!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.log('Response:', response.data);
      
      closeModal();
    } catch (error) {
      console.error('Error mengirim laporan:', error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error('Gagal mengirim laporan: ' + error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error('Gagal mengirim laporan. Silakan coba lagi.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPetugas();
  }, []);

  useEffect(() => {
    return () => {
      if (isModalOpen) {
        setIsModalOpen(false);
        setIsClosing(false);
      }
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <>
      <button
        onClick={openModal}
        className="p-2 text-danger hover:text-danger/80 hover:bg-danger/10 rounded-lg transition-all duration-200"
        title="Laporkan Kerusakan"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </button>

      {/* Modal menggunakan Portal untuk render di luar tabel */}
      {isModalOpen && document.body && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 transition-opacity duration-300"
            onClick={closeModal}
          ></div>
          
          {/* Modal Content - Bottom sheet on mobile, centered on desktop */}
          <div className={`relative animate__animated ${
              isClosing 
                ? 'animate__zoomOut animate__faster sm:animate__zoomOut animate__slideOutDown' 
                : 'sm:animate__bounceIn animate__slideInUp'
            } bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-y-auto`}
          >
            {/* Header - sticky & compact on mobile */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-danger/10 rounded-lg">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-danger"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Laporkan Kerusakan
                </h3>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              {/* Jenis Kerusakan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Jenis Kerusakan
                </label>
                <SipDropdown
                  name="jenis_kerusakan"
                  value={formData.jenis_kerusakan}
                  onChange={handleInputChange}
                  placeholder="Pilih jenis kerusakan"
                  filter={false}
                  options={[
                    { label: "Hardware", value: "hardware" },
                    { label: "Software", value: "software" },
                    { label: "Jaringan", value: "jaringan" },
                    { label: "Listrik", value: "listrik" },
                    { label: "Lainnya", value: "lainnya" },
                  ]}
                />
              </div>

              {/* Deskripsi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Deskripsi Kerusakan
                </label>
                <SipTextarea
                  name="deskripsi"
                  value={formData.deskripsi}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Jelaskan detail kerusakan yang terjadi..."
                  required
                />
              </div>

              {/* Tingkat Prioritas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tingkat Prioritas
                </label>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { value: "rendah", label: "Rendah", color: "bg-green-100 text-green-800 ring-green-500" },
                    { value: "sedang", label: "Sedang", color: "bg-yellow-100 text-yellow-800 ring-yellow-500" },
                    { value: "tinggi", label: "Tinggi", color: "bg-red-100 text-red-800 ring-red-500" }
                  ].map((priority) => (
                    <label key={priority.value}>
                      <input
                        type="radio"
                        name="tingkat_prioritas"
                        value={priority.value}
                        checked={formData.tingkat_prioritas === priority.value}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`p-2 sm:p-2.5 text-center text-xs sm:text-sm font-medium rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.tingkat_prioritas === priority.value 
                          ? priority.color + " ring-2 ring-offset-1" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                        {priority.label}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pilih Petugas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Petugas yang Bertanggung Jawab
                </label>
                <SipDropdown
                  name="petugas"
                  value={formData.petugas}
                  onChange={handleInputChange}
                  placeholder="Pilih petugas"
                  options={(petugas || []).map((item) => ({
                    label: `${item.nama} - ${item.jabatan}`,
                    value: item.nama,
                  }))}
                />
              </div>

              {/* Upload Foto */}
              <ImageUpload
                label="Foto Kerusakan"
                name="foto"
                value={formData.foto}
                onChange={handleImageChange}
                optional
                previewSize="sm"
              />

              {/* Action Buttons - sticky on mobile */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4 sticky bottom-0 bg-white pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="order-2 sm:order-1 px-4 py-2.5 sm:py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`order-1 sm:order-2 px-4 py-2.5 sm:py-2 text-white rounded-lg text-sm transition-colors duration-200 font-medium ${
                    isLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-danger hover:bg-danger/90 active:scale-[0.98]'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Mengirim...
                    </div>
                  ) : (
                    'Kirim Laporan'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Toast Container */}
      <ToastContainer />
    </>
  );
}

export default TindakanComponent;